import axiosInstance from '@/app/api/axiosInstance';
import logoUrl from '/resources/company-logo.png';

// Dynamic import for html2pdf to avoid SSR issues
const loadHtml2Pdf = async () => {
  if (typeof window !== 'undefined') {
    const module = await import('html2pdf.js');
    return module.default;
  }
  throw new Error('html2pdf requires a browser environment');
};

// Interface matching backend response structure for vouchers/invoices
export interface PrintInvoiceItemData {
  serial_no: number;
  name: string;
  name_local: string | null;
  hsn_code: string;
  quantity: string;
  unit: string;
  price_per_unit: string;
  final_price: string;
  total_amount: string;
  discount_percent: string;
  discount_amount: string;
  tax_rate: string;
  taxable_amount: string;
  cgst_rate: string;
  cgst_amount: string;
  sgst_rate: string;
  sgst_amount: string;
  igst_rate: string;
  igst_amount: string;
}

export interface TaxSummaryData {
  hsn_sac: string;
  taxable_amount: string;
  cgst_rate: string;
  cgst_amount: string;
  sgst_rate: string;
  sgst_amount: string;
  igst_rate: string;
  igst_amount: string;
  total_tax: string;
}

export interface SalesInvoicePrintData {
  document: {
    type?: string;
    voucher_type?: string;
    voucher_type_code?: string;
    invoice_number?: string;
    voucher_number?: string;
    date: string;
    due_date: string | null;
    place_of_supply: string;
    place_of_supply_code: string;
    payment_mode: string | null;
    payment_terms: string | null;
    reference_number: string | null;
    e_way_bill_number?: string | null;
    notes?: string | null;
    narration?: string | null;
  };
  company: {
    name: string;
    suffix: string | null;
    address_line1: string;
    address_line2: string;
    phone: string;
    email: string | null;
    gstin: string;
    state: string;
    state_code: string;
    logo_url: string | null;
    pan: string;
  };
  party?: {
    name: string;
    billing_address: string;
    shipping_address: string;
    contact_number: string;
    email: string;
    gstin: string;
    state: string;
    state_code: string;
    pan: string | null;
  };
  customer?: {
    name: string;
    billing_address: string;
    shipping_address: string;
    contact_number: string;
    email: string;
    gstin: string;
    state: string;
    state_code: string;
    pan: string | null;
  };
  items: PrintInvoiceItemData[];
  tax_summary: TaxSummaryData[];
  totals: {
    items_total: string;
    items_count: number;
    sub_total: string;
    total_taxable: string;
    total_cgst: string;
    total_sgst: string;
    total_igst: string;
    total_tax: string;
    total_discount: string;
    delivery_charge?: string;
    grand_total: string;
    advance_amount?: string;
    balance_amount?: string;
    amount_paid?: string;
    amount_due?: string;
    amount_in_words: string;
  };
}

// Fetch print data from backend
export const getSalesInvoicePrintData = async (id: number): Promise<SalesInvoicePrintData> => {
  try {
    const response = await axiosInstance.get<SalesInvoicePrintData>(`/vouchers/${id}/print-data`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get sales invoice print data for ID ${id}:`, error);
    throw error;
  }
};

// Generate PDF from HTML
export const generateSalesInvoicePDF = async (printData: SalesInvoicePrintData, filename?: string): Promise<void> => {
  const html = generateSalesInvoiceHTML(printData, true); // true for PDF mode
  
  // Create a temporary container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.innerHTML = html;
  document.body.appendChild(container);
  
  // Get the actual content element
  const contentElement = container.querySelector('.invoice-box') || container.querySelector('body') || container;
  
  const options = {
    margin: [3, 5, 3, 5], // top, left, bottom, right in mm - reduced top/bottom for better space usage
    filename: filename || `Invoice-${printData.document.voucher_number || printData.document.invoice_number || 'draft'}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      windowWidth: 800,
      windowHeight: 1200
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' as const,
      compress: true
    },
    pagebreak: { 
      mode: ['css', 'legacy'], // Removed 'avoid-all' to allow natural page breaks
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['.avoid-break', '.no-break'] // Reduced restrictions
    }
  };
  
  try {
    // Load html2pdf dynamically
    const html2pdf = await loadHtml2Pdf();
    
    // Generate PDF - use the content element directly
    await html2pdf().set(options).from(contentElement as HTMLElement).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
};

// Open PDF in new window for preview
export const previewSalesInvoicePDF = async (printData: SalesInvoicePrintData): Promise<void> => {
  const html = generateSalesInvoiceHTML(printData, true); // true for PDF mode
  
  // Create a temporary container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.innerHTML = html;
  document.body.appendChild(container);
  
  // Get the actual content element
  const contentElement = container.querySelector('.invoice-box') || container.querySelector('body') || container;
  
  const options = {
    margin: [3, 5, 3, 5], // Reduced top/bottom margins
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      windowWidth: 800,
      windowHeight: 1200
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' as const,
      compress: true
    },
    pagebreak: { 
      mode: ['css', 'legacy'], // Removed 'avoid-all'
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['.avoid-break', '.no-break'] // Simplified
    }
  };
  
  try {
    // Load html2pdf dynamically
    const html2pdf = await loadHtml2Pdf();
    
    // Generate PDF and open in new window
    const pdfBlob = await html2pdf().set(options).from(contentElement as HTMLElement).outputPdf('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    // Clean up URL after delay
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
  } catch (error) {
    console.error('Error generating PDF preview:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
};

// Generate HTML from print data
export const generateSalesInvoiceHTML = (printData: SalesInvoicePrintData, forPdf: boolean = false, taxExclusive: boolean = false): string => {
  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };
  
  // For PDF generation, we need the full URL
  const logoSrc = forPdf ? `${window.location.origin}${logoUrl}` : logoUrl;
  
  // Handle both party and customer fields (voucher endpoint uses 'party', sales order uses 'customer')
  const customerData = printData.party || printData.customer || {
    name: '',
    billing_address: '',
    shipping_address: '',
    contact_number: '',
    email: '',
    gstin: '',
    state: '',
    state_code: '',
    pan: null
  };
  
  // Handle different document number fields
  const invoiceNumber = printData.document.voucher_number || printData.document.invoice_number || 'DRAFT';
  const documentNotes = printData.document.notes || printData.document.narration || '';
  
  // Generate items HTML
  const itemsHTML = printData.items.map(item => {
    const displayName = item.name_local ? `${item.name} - ${item.name_local}` : item.name;

    // Calculate values based on tax mode
    const pricePerUnit = taxExclusive
      ? parseFloat(item.taxable_amount) / parseFloat(item.quantity)
      : parseFloat(item.price_per_unit);
    const amount = taxExclusive
      ? parseFloat(item.taxable_amount)
      : parseFloat(item.total_amount);

    return `
    <tr class="border-b">
      <td class="p-2">${item.serial_no}</td>
      <td class="p-2">${displayName}</td>
      <td class="p-2 text-right">${parseFloat(item.quantity).toFixed(3).replace(/\.?0+$/, '')}</td>
      <td class="p-2 text-right">₹ ${formatCurrency(pricePerUnit)}</td>
      <td class="p-2 text-center">${item.hsn_code || '-'}</td>
      <td class="p-2 text-center">${item.tax_rate ? (parseFloat(item.tax_rate) % 1 === 0 ? parseFloat(item.tax_rate).toFixed(0) : parseFloat(item.tax_rate).toFixed(1)) + '%' : '-'}</td>
      <td class="p-2 text-right">₹ ${formatCurrency(amount)}</td>
    </tr>
  `;
  }).join('');
  
  // Generate tax summary HTML
  const taxSummaryHTML = printData.tax_summary.map(tax => `
    <tr class="border-b text-right">
      <td class="p-2 border-r text-left">${tax.hsn_sac}</td>
      <td class="p-2 border-r">₹ ${formatCurrency(tax.taxable_amount)}</td>
      <td class="p-2 border-r">${parseFloat(tax.cgst_rate).toFixed(1)}%</td>
      <td class="p-2 border-r">₹ ${formatCurrency(tax.cgst_amount)}</td>
      <td class="p-2 border-r">${parseFloat(tax.sgst_rate).toFixed(1)}%</td>
      <td class="p-2 border-r">₹ ${formatCurrency(tax.sgst_amount)}</td>
      <td class="p-2">₹ ${formatCurrency(tax.total_tax)}</td>
    </tr>
  `).join('');
  
  const companySuffix = '';
  const paymentMode = printData.document.payment_mode || 'Credit';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sales Invoice - ${invoiceNumber}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: 'Inter', 'Arial', sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
        }
        .invoice-box {
            max-width: 800px;
            margin: auto;
            padding: 20px;
            background: white;
        }
        .text-xs { font-size: 10px; }
        .text-sm { font-size: 11px; }
        .text-base { font-size: 12px; }
        .text-lg { font-size: 14px; }
        .text-xl { font-size: 16px; }
        
        /* Reduce font sizes for PDF - but not too small */
        ${forPdf ? `
        body { font-size: 11px; }
        .text-xs { font-size: 9px; }
        .text-sm { font-size: 10px; }
        .text-base { font-size: 11px; }
        .text-lg { font-size: 13px; }
        .text-xl { font-size: 15px; }
        .invoice-box { padding: 15px; }
        .p-2 { padding: 3px !important; }
        .p-4 { padding: 6px !important; }
        .mb-4 { margin-bottom: 8px !important; }
        .mb-6 { margin-bottom: 10px !important; }
        .mb-8 { margin-bottom: 12px !important; }
        table { font-size: 10px; }
        th { font-size: 10px; }
        
        /* Page break handling */
        tr { page-break-inside: auto; }
        table { page-break-inside: auto; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        .avoid-break { page-break-inside: avoid; }
        .no-break { page-break-inside: avoid; }
        ` : ''}
        
        @media print {
            body {
                margin: 0;
                padding: 0;
            }
            .invoice-box {
                max-width: 100%;
                margin: 0;
                padding: 8px;
                border: none;
                box-shadow: none;
            }
            /* Table handling for print */
            table { 
                page-break-inside: auto;
                border-collapse: collapse;
            }
            thead { 
                display: table-header-group;
                page-break-inside: avoid;
                page-break-after: avoid;
            }
            tbody tr {
                page-break-inside: auto;
                page-break-after: auto;
            }
            /* Keep specific elements together */
            .avoid-break { page-break-inside: avoid; }
            .no-break { page-break-inside: avoid; }
            /* Items total row styling */
            .items-total-row { 
                page-break-inside: avoid;
                font-weight: bold;
            }
        }
        @page {
            margin: 8mm;
            size: A4;
        }
    </style>
</head>
<body class="bg-gray-100 p-4 sm:p-8">
    <div class="invoice-box bg-white">
        <!-- Header -->
        <div class="text-center font-bold text-xl mb-4">Tax Invoice</div>

        <!-- Company Info and Invoice Details -->
        <table class="w-full mb-6">
            <tr>
                <td class="w-1/2" style="vertical-align: top;">
                    <div class="flex items-start">
                        <img src="${logoSrc}" alt="Company Logo" class="w-16 h-16 mr-4 object-contain">
                        <div>
                            <h1 class="text-lg font-bold text-gray-800">${printData.company.name} ${companySuffix}</h1>
                            <p class="text-xs text-gray-600">
                                ${printData.company.address_line1}<br>
                                ${printData.company.address_line2}<br>
                                Phone: ${printData.company.phone}<br>
                                GSTIN: ${printData.company.gstin}<br>
                                State: ${printData.company.state_code}-${printData.company.state}
                            </p>
                        </div>
                    </div>
                </td>
                <td class="w-1/2 text-right" style="vertical-align: top;">
                    <table class="w-full">
                        <tr>
                            <td class="text-right font-semibold">Invoice No:</td>
                            <td class="text-left pl-4">${invoiceNumber}</td>
                        </tr>
                        <tr>
                            <td class="text-right font-semibold">Date:</td>
                            <td class="text-left pl-4">${printData.document.date}</td>
                        </tr>
                        <tr>
                            <td class="text-right font-semibold">Place of Supply:</td>
                            <td class="text-left pl-4">${printData.document.place_of_supply}</td>
                        </tr>
                        <tr>
                            <td class="text-right font-semibold">Due Date:</td>
                            <td class="text-left pl-4">${printData.document.due_date || '-'}</td>
                        </tr>
                        ${printData.document.reference_number ? `
                        <tr>
                            <td class="text-right font-semibold">Reference:</td>
                            <td class="text-left pl-4">${printData.document.reference_number}</td>
                        </tr>
                        ` : ''}
                        ${printData.document.e_way_bill_number ? `
                        <tr>
                            <td class="text-right font-semibold">E-Way Bill No:</td>
                            <td class="text-left pl-4">${printData.document.e_way_bill_number}</td>
                        </tr>
                        ` : ''}
                    </table>
                </td>
            </tr>
        </table>

        <!-- Bill To -->
        <div class="mb-8">
            <div class="border rounded-lg p-4">
                <h3 class="${forPdf ? 'font-medium text-sm' : 'font-semibold'} text-gray-700 mb-2">Bill To</h3>
                <p class="font-bold text-gray-800">${customerData.name}</p>
                <p class="text-gray-600">
                    ${customerData.billing_address ? `${customerData.billing_address}<br>` : ''}
                    ${customerData.contact_number ? `Contact No.: ${customerData.contact_number}<br>` : ''}
                    ${customerData.gstin ? `GSTIN Number: ${customerData.gstin}<br>` : ''}
                    ${customerData.state ? `State: ${customerData.state_code}-${customerData.state}` : ''}
                </p>
            </div>
        </div>

        <!-- Items Table -->
        <table class="w-full ${forPdf ? 'text-xs' : 'text-sm'} mb-8">
            <thead class="bg-gray-100">
                <tr class="text-left text-gray-600">
                    <th class="p-2 font-semibold">#</th>
                    <th class="p-2 font-semibold">Item name</th>
                    <th class="p-2 font-semibold text-right">Quantity</th>
                    <th class="p-2 font-semibold text-right">Price/ Unit${taxExclusive ? ' (Excl. Tax)' : ''}</th>
                    <th class="p-2 font-semibold text-center">HSN</th>
                    <th class="p-2 font-semibold text-center">Tax %</th>
                    <th class="p-2 font-semibold text-right">Amount ${taxExclusive ? '(Excl. Tax)' : '(Incl. Tax)'}</th>
                </tr>
            </thead>
            <tbody>
                ${itemsHTML}
                <!-- Total row -->
                <tr class="font-bold border-t no-break">
                    <td colspan="2" class="p-2">Total</td>
                    <td class="p-2 text-right">${parseFloat(printData.totals.items_total).toFixed(3).replace(/\.?0+$/, '')}</td>
                    <td colspan="3" class="p-2"></td>
                    <td class="p-2 text-right">₹ ${formatCurrency(taxExclusive ? printData.totals.total_taxable : printData.totals.grand_total)}</td>
                </tr>
            </tbody>
        </table>

        <!-- Amounts Section -->
        <div class="flex justify-between mb-8">
            <div class="w-1/2 pr-4">
                <p class="font-semibold">Invoice Amount In Words</p>
                <p>${printData.totals.amount_in_words}</p>
                <p class="font-semibold mt-4">Payment Mode</p>
                <p>${paymentMode}</p>
            </div>
            <div class="w-1/2 pl-4">
                <table class="w-full">
                    <tbody>
                        ${taxExclusive ? `
                        <tr>
                            <td class="text-right p-1">Sub Total (Excl. Tax)</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.total_taxable)}</td>
                        </tr>
                        <tr>
                            <td class="text-right p-1">+ Tax</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.total_tax)}</td>
                        </tr>
                        ${printData.totals.delivery_charge && parseFloat(printData.totals.delivery_charge) > 0 ? `
                        <tr>
                            <td class="text-right p-1">+ Delivery Charge</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.delivery_charge)}</td>
                        </tr>
                        ` : ''}
                        <tr class="font-bold">
                            <td class="text-right p-1 border-t">Total (Incl. Tax)</td>
                            <td class="text-right p-1 border-t">₹ ${formatCurrency(printData.totals.grand_total)}</td>
                        </tr>
                        ` : `
                        <tr>
                            <td class="text-right p-1">Sub Total</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.sub_total)}</td>
                        </tr>
                        ${printData.totals.delivery_charge && parseFloat(printData.totals.delivery_charge) > 0 ? `
                        <tr>
                            <td class="text-right p-1">Delivery Charge</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.delivery_charge)}</td>
                        </tr>
                        ` : ''}
                        <tr class="font-bold">
                            <td class="text-right p-1 border-t">Total</td>
                            <td class="text-right p-1 border-t">₹ ${formatCurrency(printData.totals.grand_total)}</td>
                        </tr>
                        `}
                        <tr>
                            <td class="text-right p-1">Received</td>
                            <td class="text-right p-1">₹ ${formatCurrency(printData.totals.advance_amount || printData.totals.amount_paid || '0')}</td>
                        </tr>
                        <tr class="font-bold">
                            <td class="text-right p-1 border-t">Balance</td>
                            <td class="text-right p-1 border-t">₹ ${formatCurrency(printData.totals.balance_amount || printData.totals.amount_due || printData.totals.grand_total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Tax Table -->
        <table class="w-full ${forPdf ? 'text-xs' : 'text-sm'} mb-8 border avoid-break">
            <thead class="bg-gray-100">
                <tr class="text-center text-gray-600">
                    <th rowspan="2" class="p-2 font-semibold border-r">HSN/ SAC</th>
                    <th rowspan="2" class="p-2 font-semibold border-r">Taxable amount</th>
                    <th colspan="2" class="p-2 font-semibold border-r">CGST</th>
                    <th colspan="2" class="p-2 font-semibold border-r">SGST</th>
                    <th rowspan="2" class="p-2 font-semibold">Total Tax Amount</th>
                </tr>
                <tr class="text-center text-gray-600">
                    <th class="p-2 font-semibold border-r border-t">Rate</th>
                    <th class="p-2 font-semibold border-r border-t">Amount</th>
                    <th class="p-2 font-semibold border-r border-t">Rate</th>
                    <th class="p-2 font-semibold border-r border-t">Amount</th>
                </tr>
            </thead>
            <tbody>
                ${taxSummaryHTML}
            </tbody>
            <tfoot class="bg-gray-50 font-bold">
                <tr class="text-right">
                    <td class="p-2 border-r text-left">Total</td>
                    <td class="p-2 border-r">₹ ${formatCurrency(printData.totals.total_taxable)}</td>
                    <td class="p-2 border-r"></td>
                    <td class="p-2 border-r">₹ ${formatCurrency(printData.totals.total_cgst)}</td>
                    <td class="p-2 border-r"></td>
                    <td class="p-2 border-r">₹ ${formatCurrency(printData.totals.total_sgst)}</td>
                    <td class="p-2">₹ ${formatCurrency(printData.totals.total_tax)}</td>
                </tr>
            </tfoot>
        </table>

        <!-- Footer -->
        <div class="flex justify-between items-end avoid-break">
            <div class="w-1/2 pr-4">
                <h3 class="font-semibold text-gray-700 mb-2">Terms and conditions</h3>
                <p class="text-xs text-gray-600">Thanks for doing business with us!</p>
                ${documentNotes ? `
                <p class="text-xs text-gray-600 mt-2">Notes: ${documentNotes}</p>
                ` : ''}
            </div>
            <div class="w-1/2 pl-4 text-center">
                <p class="text-xs mb-12">For: ${printData.company.name} ${companySuffix}</p>
                <p class="border-t pt-2 text-xs font-semibold">Authorized Signatory</p>
            </div>
        </div>
    </div>
    <script>
        // Remove any 'about:blank' text that might appear
        window.onload = function() {
            if (document.body.textContent.includes('about:blank')) {
                document.body.innerHTML = document.body.innerHTML.replace(/about:blank/g, '');
            }
        };
    </script>
</body>
</html>
  `;
};

// Generate PDF using Cloudflare Worker (server-side rendering)
export const generateSalesInvoicePDFViaWorker = async (printData: SalesInvoicePrintData, filename?: string): Promise<void> => {
  try {
    // Generate HTML from print data
    const html = generateSalesInvoiceHTML(printData, true); // true for PDF mode

    // Call the worker endpoint
    const response = await fetch('/api/generate-pdf', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        html,
        filename: filename || `Invoice-${printData.document.voucher_number || printData.document.invoice_number || 'draft'}.pdf`,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Worker PDF generation failed: ${response.statusText}`);
    }

    // Get the PDF blob
    const pdfBlob = await response.blob();

    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `Invoice-${printData.document.voucher_number || printData.document.invoice_number || 'draft'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Worker PDF generation error:', error);
    throw error;
  }
};

// Preview PDF generated by Cloudflare Worker
export const previewSalesInvoicePDFViaWorker = async (printData: SalesInvoicePrintData): Promise<void> => {
  try {
    // Generate HTML from print data
    const html = generateSalesInvoiceHTML(printData, true); // true for PDF mode

    // Call the worker endpoint
    const response = await fetch('/api/generate-pdf', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        html,
        filename: `Invoice-${printData.document.voucher_number || printData.document.invoice_number || 'draft'}.pdf`,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Worker PDF generation failed: ${response.statusText}`);
    }

    // Get the PDF blob
    const pdfBlob = await response.blob();

    // Open PDF in new tab for preview
    const url = URL.createObjectURL(pdfBlob);
    const previewWindow = window.open(url, '_blank');
    if (!previewWindow) {
      URL.revokeObjectURL(url);
      throw new Error('Please allow pop-ups to preview the PDF');
    } else {
      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60000); // 1 minute
    }
  } catch (error) {
    console.error('Worker PDF preview error:', error);
    throw error;
  }
};

// Tax-Exclusive PDF Functions

// Generate and preview PDF with tax-exclusive values
export const previewSalesInvoicePDFTaxExclusive = async (printData: SalesInvoicePrintData): Promise<void> => {
  const html = generateSalesInvoiceHTML(printData, true, true); // forPdf=true, taxExclusive=true

  // Create a temporary container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.innerHTML = html;
  document.body.appendChild(container);

  // Get the actual content element
  const contentElement = container.querySelector('.invoice-box') || container.querySelector('body') || container;

  const options = {
    margin: [3, 5, 3, 5],
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      windowWidth: 800,
      windowHeight: 1200
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait' as const,
      compress: true
    },
    pagebreak: {
      mode: ['css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['.avoid-break', '.no-break']
    }
  };

  try {
    const html2pdf = await loadHtml2Pdf();
    const pdfBlob = await html2pdf().set(options).from(contentElement as HTMLElement).outputPdf('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
  } catch (error) {
    console.error('Error generating tax-exclusive PDF preview:', error);
    throw error;
  } finally {
    document.body.removeChild(container);
  }
};

// Generate and save PDF with tax-exclusive values
export const generateSalesInvoicePDFTaxExclusive = async (printData: SalesInvoicePrintData, filename?: string): Promise<void> => {
  const html = generateSalesInvoiceHTML(printData, true, true); // forPdf=true, taxExclusive=true

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.innerHTML = html;
  document.body.appendChild(container);

  const contentElement = container.querySelector('.invoice-box') || container.querySelector('body') || container;

  const options = {
    margin: [3, 5, 3, 5],
    filename: filename || `Invoice-${printData.document.voucher_number || printData.document.invoice_number || 'draft'}-tax-excl.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      windowWidth: 800,
      windowHeight: 1200
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait' as const,
      compress: true
    },
    pagebreak: {
      mode: ['css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['.avoid-break', '.no-break']
    }
  };

  try {
    const html2pdf = await loadHtml2Pdf();
    await html2pdf().set(options).from(contentElement as HTMLElement).save();
  } catch (error) {
    console.error('Error generating tax-exclusive PDF:', error);
    throw error;
  } finally {
    document.body.removeChild(container);
  }
};
